import React from "react";
import { Field, Form, FormSpy } from "react-final-form";
import {
  Button,
  ToggleSwitch,
  Tooltip,
  ToolTipPosition,
} from "@pickit/pickit-components";
import { TabControler } from "component/TabControler/TabControler";
import Back from "component/back/Back";

import { DetailPickerTypeProps } from "./types";
import { pickerTabs, PickerType } from "../pickers/types";

import useValidationSchema from "hooks/useValidationSchema";

import PersonalData from "./personalData/PersonalData";
import AccountDataCO from "./accountData/CO/AccountDataCO";
import AccountDataMX from "./accountData/MX/AccountDataMX";
import AccountDataAR from "./accountData/AR/AccountDataAR";
import VehicleDataAR from "./vehicleData/AR/VehicleDataAR";
import VehicleDataCO from "./vehicleData/CO/VehicleDataCO";

import { ReactComponent as MotorcycleIcon } from "assets/motorcycle.svg";
import { ReactComponent as BicycleIcon } from "assets/bici.svg";
import { ReactComponent as Wheels4Icon } from "assets/4_wheels.svg";

import "pages/pickers/Pickers.scss";
import "pages/detailPicker/detailPicker.scss";
import "./i18n";
import { useTranslation } from "react-i18next";
import { CountryComponentType } from "utils/types";
import VehicleDataMX from "./vehicleData/MX/VehicleDataMX";
import { VehicleDataProps } from "./vehicleData/types";
import { AccountDataProps } from "./accountData/types";
import { MOTORIZED_VEHICLES_TAGS, VehicleTagType } from "utils/constants";
import AccountDataCL from "./accountData/CL/AccountDataCL";
import VehicleDataCL from "./vehicleData/CL/VehicleDataCL";

export const DetailPicker: React.FC<DetailPickerTypeProps> = ({
  actualPage,
  dirty,
  accountTypes,
  pendingUserAdminPicker,
  initialValues,
  setDirty,
  active,
  nameDisplay,
  phoneNumber,
  validationSchema,
  wrongFiles,
  loadedFiles,
  regions,
  tabs,
  countryCode,
  fiscalNumberRef,
  hasSaved,
  formRef,
  cancel,
  goBack,
  savePicker,
  changePage,
  getBankName,
  onSubmit,
  getWorkzones,
  setFiscalNumberDuplicated,
  contactPicker,
  setHasSaved,
  ...props
}) => {
  const { t } = useTranslation();
  const { id, vehicle, personalData, files } = pendingUserAdminPicker;
  const isMotorized = MOTORIZED_VEHICLES_TAGS.includes(vehicle?.type);
  const vehicleIcon: { [key in VehicleTagType]: JSX.Element } = {
    MOTORCYCLE: <MotorcycleIcon data-testId="picker-icon-motorcycle" />,
    BICYCLE: <BicycleIcon data-testId="picker-icon-bicycle" />,
    "4_WHEELS": <Wheels4Icon data-testId="picker-icon-4-wheels" />,
  };

  const accountDataProps: AccountDataProps = {
    isActivePicker: active,
    files: files.accountingData,
    testId: "account-data",
    fiscalNumberRef,
    setFiscalNumberDuplicated,
    ...props,
  };

  const accountData: CountryComponentType = {
    AR: <AccountDataAR {...accountDataProps} />,
    CO: <AccountDataCO {...accountDataProps} accountTypes={accountTypes} />,
    MX: <AccountDataMX {...accountDataProps} />,
    CL: <AccountDataCL {...accountDataProps} />,
  };

  const vehicleDataProps: VehicleDataProps = {
    isActivePicker: active,
    files: files.vehicle,
    testId: "vehicle-data",
    vehicleType: vehicle.type,
    ...props,
  };

  const vehicleData: CountryComponentType = {
    AR: <VehicleDataAR {...vehicleDataProps} />,
    CO: <VehicleDataCO {...vehicleDataProps} />,
    MX: <VehicleDataMX {...vehicleDataProps} />,
    CL: <VehicleDataCL {...vehicleDataProps} />,
  };

  return (
    <>
      <div className="header-container">
        <TabControler<pickerTabs>
          tabs={tabs}
          changePage={(page) => changePage(page, dirty)}
          actualPage={actualPage}
        />
        <Back
          testId="picker-button-back"
          id="picker-button-back"
          onClick={() => goBack(true, dirty)}
        />
      </div>
      <div className="page-title-container">
        <div className="picker-name-container">
          <div className="picker-id">
            {id && `#${id}`}
            <h2
              className="picker-name"
              data-testId="picker-title-name"
              id="picker-title-name"
            >
              {nameDisplay}
            </h2>
          </div>
          {vehicleIcon[vehicle?.type]}
        </div>
        <Button
          hierarchy="secondary"
          label={t("picker:contactLabel")}
          disabled={!phoneNumber}
          onClick={contactPicker}
          iconName="whatsapp"
          testId={`picker-contact-button`}
        />
      </div>
      <Form
        keepDirtyOnReinitialize
        onSubmit={(values) => onSubmit(values as PickerType)}
        initialValues={initialValues}
        validate={useValidationSchema(validationSchema)}
        mutators={{
          bankSearch: ([field, value], state, { changeValue }) => {
            const values = state.formState.values as PickerType;
            const bankIdentifier = values.accountingData?.account?.number;

            if (!bankIdentifier || bankIdentifier.length < 22)
              changeValue(state, "accountingData.bankName", () => "-");
            else {
              const prefix = bankIdentifier.substring(0, 3);
              getBankName(prefix);
            }
          },
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value);
          },
          setWorkzones: ([field, value], state, { changeValue }) => {
            changeValue(state, "desiredWorkzones", () => "");
            if ((state.formState.values as PickerType)?.region?.id) {
              getWorkzones((state.formState.values as PickerType).region.id);
            }
          },
        }}
      >
        {({ invalid, handleSubmit, dirty, values, form }) => {
          formRef.current = form;
          return (
            <form onSubmit={handleSubmit}>
              <FormSpy
                subscription={{
                  dirty: true,
                  values: true,
                  invalid: true,
                }}
                onChange={(pro) => {
                  setDirty(pro.dirty);
                  setHasSaved(!pro.dirty);
                }}
              />
              <PersonalData
                {...props}
                regions={regions}
                isActivePicker={active}
                files={files.personalData}
                identificationType={personalData.identification?.name}
                testId="personal-data"
                phoneNumber={phoneNumber}
                name={nameDisplay}
                pickerTermsStatus={props.pickerTermsStatus}
              />
              <h3
                data-testId="picker-title-account-data"
                className="detail-picker-section-title"
              >
                {t("picker:accountTitle")}
              </h3>
              {accountData[countryCode]}
              {isMotorized && (
                <>
                  <h3
                    data-testId="picker-title-vehicle-data"
                    className="detail-picker-section-title"
                  >
                    {t("picker:vehicleTitle")}
                  </h3>
                  {vehicleData[countryCode]}
                </>
              )}
              {active ? (
                <div className="active-bottom-container">
                  <div className="toggles-container">
                    <div className="switch-container">
                      <h3 className="switch-title" data-testId="active-picker-state-label">{t("picker:state")}</h3>
                      <Field
                        dataTestId="active-picker-state-switch"
                        name="enable"
                        component={ToggleSwitch}
                      />
                    </div>
                    <div className="switch-container">
                      <h3 className="switch-title" data-testId="fullranking-picker-state-label">{t("picker:pickerFull")}</h3>
                      <Field
                        dataTestId="fullranking-picker-state-switch"
                        name="ranking.fullRanking"
                        component={ToggleSwitch}
                      />
                    </div>
                  </div>
                  <div className="picker-buttons-container">
                    <div className="picker-button">
                      <Button
                        type="button"
                        testId="active-picker-button-restore"
                        onClick={() => cancel(dirty, form)}
                        disabled={!dirty || hasSaved}
                        label={t("global:restore")}
                        size="large"
                        hierarchy="secondary"
                        fullWidth
                      />
                    </div>
                    <div className="picker-button">
                      <Button
                        type="submit"
                        testId="active-picker-button-save"
                        disabled={invalid || !dirty || hasSaved}
                        label={t("global:save")}
                        size="large"
                        fullWidth
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="pending-bottom-container">
                  <div className="picker-buttons-container">
                    <div className="picker-button">
                      <Button
                        type="button"
                        testId="pending-picker-button-save"
                        onClick={() => savePicker(values as PickerType)}
                        label={t("global:saveChanges")}
                        size="large"
                        hierarchy="secondary"
                        fullWidth
                      />
                    </div>
                    <Tooltip
                      message={t("picker:approveTooltip")}
                      disabled={
                        !invalid && !wrongFiles && loadedFiles && hasSaved
                      }
                      position={ToolTipPosition.top}
                    >
                      <div className="picker-button">
                        <Button
                          type="submit"
                          testId="active-picker-button-approve"
                          disabled={
                            invalid || wrongFiles || !loadedFiles || !hasSaved
                          }
                          label={t("picker:approvePicker")}
                          size="large"
                          fullWidth
                        />
                      </div>
                    </Tooltip>
                  </div>
                </div>
              )}
            </form>
          );
        }}
      </Form>
    </>
  );
};
